import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppSelect from '@nxsys/components/AppSelect';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import TaxWeekMonthSelect from '@nxsys/components/AppTaxWeekMonthSelect';
import AppUploadFile from '@nxsys/components/AppUploadFile';
import {
  expenseMilesOptions,
  expenseTypeOptions,
} from '@nxsys/constants/AppConstants';
import { ExpenseTypeEnum } from '@nxsys/constants/AppEnums';
import { useCalculateExpenseTotal } from '@nxsys/hooks/api';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Option } from '@nxsys/types/models/Common';
import { Form } from 'antd';
import { FormProps } from 'antd/lib';
import { debounce, toNumber } from 'lodash';
import { Moment } from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import AppCurrencyInput from '@nxsys/components/AppCurrencyInput';

type CreateExpenseModalProps = {
  isOpen: boolean;
  toggle: () => void;
  onSubmit: (params: CreateExpenseValues) => void | Promise<void>;
  companyId: string;
  employeeId: string;
};

export type CreateExpenseValues = {
  date: Moment | null;
  document: File | string | null;
  description: string;
  taxPeriod: number;
  claimAmount: string;
  type: Option<string> | null;
  expenseType: Option<string> | null;
  total: number;
};

const CreateExpenseModal: React.FC<CreateExpenseModalProps> = ({
  isOpen,
  toggle,
  onSubmit,
  companyId,
  employeeId,
}: CreateExpenseModalProps) => {
  const { messages } = useTypedIntl();
  const [form] = Form.useForm<CreateExpenseValues>();
  const watchValues = Form.useWatch([], form);

  const {
    loading: calculateExpenseTotalLoading,
    run: calculateExpenseTotalRun,
  } = useCalculateExpenseTotal();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const onFinish: FormProps['onFinish'] = async (values) => {
    try {
      setIsSubmitting(true);
      await onSubmit(values);
      form.resetFields();
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  const handleCalculateExpenseTotal = useCallback(
    debounce(
      async (
        employeeId: string,
        date: string,
        amountClaim: number,
        type: string,
      ) => {
        const res = await calculateExpenseTotalRun(employeeId, {
          date,
          amountClaim,
          type,
        });
        form.setFieldValue('total', res.total);
      },
      500,
    ),
    [],
  );

  useEffect(() => {
    if (watchValues?.date && watchValues?.claimAmount && watchValues?.type) {
      handleCalculateExpenseTotal(
        employeeId,
        watchValues?.date.toISOString(),
        toNumber(watchValues?.claimAmount),
        watchValues?.type.value,
      );
    }
  }, [watchValues?.date, watchValues?.claimAmount, watchValues?.type]);

  return (
    <AppModalContainer
      title={messages['common.createExpense']}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.save'],
          onClick: () => form.submit(),
          loading: isSubmitting,
          disabled: isSubmitting || calculateExpenseTotalLoading,
        },
      ]}
    >
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
      >
        <Form.Item
          label={messages['common.date']}
          name="date"
          rules={[{ required: true, message: messages['form.pleaseSelect'] }]}
        >
          <AppDatePicker
            placeholder={messages['form.selectDate']}
            format="DD/MM/YYYY"
          />
        </Form.Item>
        <Form.Item label={messages['common.taxPeriod']} name="taxPeriod">
          <TaxWeekMonthSelect
            placeholder={messages['form.select']}
            onFilter={(value) => {
              form.setFieldsValue({ taxPeriod: value.taxPeriod });
            }}
          />
        </Form.Item>
        <Form.Item
          label={messages['common.expenseType']}
          name="expenseType"
          rules={[{ required: true, message: messages['form.pleaseSelect'] }]}
        >
          <AppSelect
            options={expenseTypeOptions}
            placeholder={messages['form.select']}
          />
        </Form.Item>
        <Form.Item label={messages['common.description']} name="description">
          <AppInput placeholder={messages['form.enter']} />
        </Form.Item>
        {watchValues?.expenseType?.value && (
          <Form.Item
            label={messages['common.item']}
            name="type"
            rules={[{ required: true, message: messages['form.pleaseSelect'] }]}
          >
            {watchValues?.expenseType?.value === ExpenseTypeEnum.MILEAGE ? (
              <AppSelect
                options={expenseMilesOptions}
                placeholder={messages['form.select']}
              />
            ) : (
              <AppSelectSync
                placeholder={messages['form.select']}
                apiType="searchExpenseCompany"
                type="expense"
                searchId={companyId}
                onChangeValue={(value: any) => {
                  form.setFieldsValue({ type: value.value });
                }}
              />
            )}
          </Form.Item>
        )}
        <Form.Item
          label={messages['common.attachmentDocument']}
          name="document"
          rules={[{ required: true, message: messages['form.pleaseSelect'] }]}
        >
          <AppUploadFile
            urlFile={
              typeof watchValues?.document === 'string'
                ? watchValues?.document
                : undefined
            }
            onChange={(file: File) => {
              form.setFieldsValue({ document: file });
            }}
          />
        </Form.Item>
        <Form.Item
          label={messages['common.amountClaimed']}
          name="claimAmount"
          rules={[{ required: true, message: messages['form.pleaseEnter'] }]}
        >
          <AppCurrencyInput placeholder={messages['form.enter']} />
        </Form.Item>
        <Form.Item label={messages['common.total']} name="total">
          <AppCurrencyInput
            placeholder={messages['form.enter']}
            disabled
          />
        </Form.Item>
      </AppFormContainer>
    </AppModalContainer>
  );
};

export default CreateExpenseModal;
